// The sky is a score — the pure half of the sound layer. Scales, the tone
// field's arithmetic, the pad's chords, voicings and instruments, the
// aircraft voices, the conductor's phase table, harmonic drift, and each
// city's resolved settings. No Web Audio in here, so every rule is checkable
// in Node (tests/music.test.ts); the graph that plays it is src/app/sound.ts.
// The concept and the tuning log are docs/research/music.md; this is a port
// of the playable study, docs/prototype/quiet-skies-sound-v1.html, minus its
// tuning drawer and its own little world.
import type { ChordCycle, Drift, DroneRegister, LevelDef, MusicConfig, PadVoice, PadVoicing, ScaleName } from '../sim/index.ts';

// Rings around a threshold, one scale degree each. Ring 0 is the threshold
// itself: the tonic. Far away is high and airy, close is low and resolved, so
// every approach is a descent and every landing a cadence. Scales are
// pentatonic so any two notes in the sky agree; the two marked "semitone"
// break that rule on purpose, for a darker city.
export const SCALES: Record<ScaleName, readonly number[]> = {
  'major pentatonic': [0, 2, 4, 7, 9],
  'minor pentatonic': [0, 3, 5, 7, 10],
  'suspended': [0, 2, 5, 7, 10],
  'yo': [0, 2, 5, 7, 9],
  'kumoi': [0, 2, 3, 7, 9],
  'lydian pentatonic': [0, 2, 4, 6, 9],
  'insen (semitone)': [0, 1, 5, 7, 10],
  'hirajoshi (semitone)': [0, 2, 3, 7, 8],
};

/** rings: two octaves and a bit */
export const MAX_DEG = 12;

type CityKey = 'root' | 'mode' | 'bpm' | 'wind' | 'verb' | 'bright';

/** The base every city starts from; a level's `music` block overrides what was tuned for it. */
export const BASE: Omit<MusicConfig, CityKey> = {
  chordBars: 8,
  ring: 60, tick: 56, calls: 2, landing: 'tonic + fifth',
  drone: 0.08, droneReg: 'root, deep', droneMove: 'follows the chord', droneSwell: 0.5, droneRest: 0.25, droneCut: 240, droneShimmer: 6,
  pad: 0.05, padType: 'organ', padVoicing: 'close', padProg: 'i vi ii v', padMove: 0.5, padGlide: 0.9,
  pulse: 1, sparks: 0.1,
  space: 3.4,
  journey: 'full', phaseLen: 90, drift: 'relative', driftEvery: '2', motif: 1,
};

/** a level with no `music` block is in prague's key */
export const HOME_KEY: Pick<MusicConfig, CityKey> = { root: 68, mode: 'major pentatonic', bpm: 62, wind: 0.4, verb: 1.05, bright: 3000 };

export function resolveMusic(level: Pick<LevelDef, 'music'>): MusicConfig {
  return { ...BASE, ...HOME_KEY, ...level.music };
}

/** where the drone sits, in semitones from the root: a fifth below the root is a dominant
 *  pedal, which keeps the ground without spelling the tonic out all the time */
export const REG: Record<DroneRegister, number> = { 'root, deep': -24, 'root, low': -12, 'fifth, deep': -17, 'fifth, low': -5 };

/** pad chords as scale-degree sets (negative = below the root), so they stay inside the field */
export interface Chord { n: string; d: readonly number[] }
export const PROGS: Record<ChordCycle, readonly Chord[]> = {
  'i vi ii v': [{ n: 'i', d: [0, 2, 4] }, { n: 'vi', d: [-1, 0, 2] }, { n: 'ii', d: [1, 3, 4] }, { n: 'v', d: [-2, 0, 1] }],
  'i v':       [{ n: 'i', d: [0, 2, 4] }, { n: 'v', d: [-2, 0, 1] }],
  'rising':    [{ n: 'i', d: [0, 2, 4] }, { n: 'ii', d: [1, 3, 5] }, { n: 'iii', d: [2, 4, 6] }, { n: 'ii', d: [1, 3, 5] }],
  'one chord': [{ n: 'i', d: [0, 2, 4] }],
};

/** where the three chord tones sit, in semitones from the root, per voice */
export const VOICING: Record<PadVoicing, readonly [number, number, number]> = {
  close: [-12, -12, -12], open: [-24, -12, 0], low: [-24, -24, -12], high: [0, 0, 0],
};

export type Wave = 'sine' | 'triangle' | 'sawtooth' | 'noise';
/** pad instruments: each chord tone is these partials [ratio, amplitude, detune cents, wave],
 *  through an optional lowpass (`cut`, swept by the pad's slow LFO) and an optional tremolo;
 *  `level` evens the voices out */
export interface PadSpec { level: number; cut: number; trem?: number; partials: readonly (readonly [number, number, number, Wave])[] }
export const PADS: Record<PadVoice, PadSpec> = {
  warm:    { level: 1.0,  cut: 520,  partials: [[1, 1, -7, 'triangle'], [1, 1, 7, 'triangle']] },
  strings: { level: 0.3,  cut: 420,  partials: [[1, 1, -9, 'sawtooth'], [1, 1, 0, 'sawtooth'], [1, 1, 9, 'sawtooth']] },
  glass:   { level: 0.85, cut: 2400, partials: [[1, 1, -4, 'sine'], [1, 1, 4, 'sine'], [2, 0.35, 3, 'sine'], [3, 0.12, -3, 'sine']] },
  organ:   { level: 0.55, cut: 0, trem: 0.7, partials: [[1, 1, 0, 'sine'], [2, 0.6, 0, 'sine'], [3, 0.4, 0, 'sine'], [4, 0.25, 0, 'sine']] },
  breath:  { level: 150,  cut: 0, partials: [[1, 1, 0, 'noise'], [2, 0.5, 0, 'noise']] }, // pink noise through a narrow bandpass at each tone
};
/** which partial indices (0-based) get their own slow amplitude LFO ("drawbar drift"), and the
 *  periods (seconds, primes so they never line up) assigned to each in order */
export const DRAWBAR_IDX: Partial<Record<PadVoice, readonly number[]>> = { organ: [1, 2, 3], glass: [2, 3] };
export const DRAWBAR_PERIODS: readonly number[] = [97, 151, 233];

/** Each aircraft type is an instrument: partials as [ratio, amplitude, decay seconds]. */
export interface VoiceSpec { partials: readonly (readonly [number, number, number])[]; dur: number; attack?: number; trem?: number; wave?: OscillatorType }
export const VOICES: Record<string, VoiceSpec> = {
  light:  { partials: [[1, 1, 0.55], [4, 0.22, 0.12], [9.2, 0.06, 0.05]], dur: 0.6 },                    // soft marimba
  medium: { partials: [[1, 1, 0.9], [2, 0.3, 0.18], [5.4, 0.08, 0.06]], dur: 0.95 },                     // kalimba
  heavy:  { partials: [[0.5, 0.8, 2.0], [1, 0.5, 1.5], [1.5, 0.1, 0.6]], dur: 2.1, attack: 0.03 },       // warm low pluck
  jet:    { partials: [[1, 0.75, 0.45], [3, 0.2, 0.14], [6.3, 0.07, 0.05]], dur: 0.5 },                  // glass
  heli:   { partials: [[1, 0.8, 0.75], [2, 0.16, 0.3]], dur: 0.8, trem: 7, wave: 'triangle' },           // rotor tremolo
};

// ---------- the conductor: a slow arc over tens of minutes ----------
// Named phases the city's bed walks through, dawn to night and back to dawn.
// `mul` multiplies the city's *tuned* values (never overwrites them);
// register/voicing/cycle are the pad's discrete choice while the journey is
// full; `motif` says whether the slow arpeggio may play in this phase.
export interface PhaseMul { pad: number; move: number; drone: number; wind: number; sparks: number; bright: number; verb: number }
export interface Phase { name: string; mul: PhaseMul; register: DroneRegister; voicing: PadVoicing; cycle: ChordCycle; motif: boolean }
export const PHASES: readonly Phase[] = [
  { name: 'dawn',    mul: { pad: 0.3, move: 0.5, drone: 1,   wind: 1,   sparks: 0.3,  bright: 0.7, verb: 1   }, register: 'root, deep',  voicing: 'close', cycle: 'one chord',  motif: false },
  { name: 'morning', mul: { pad: 0.7, move: 1,   drone: 1,   wind: 1,   sparks: 0.6,  bright: 0.9, verb: 1   }, register: 'root, deep',  voicing: 'close', cycle: 'i v',        motif: false },
  { name: 'day',     mul: { pad: 1,   move: 1,   drone: 1,   wind: 1,   sparks: 1,    bright: 1,   verb: 1   }, register: 'root, deep',  voicing: 'close', cycle: 'i vi ii v',  motif: true  },
  { name: 'high',    mul: { pad: 1.1, move: 1.5, drone: 0.8, wind: 0.9, sparks: 1.2,  bright: 1.1, verb: 1.2 }, register: 'root, low',   voicing: 'open',  cycle: 'rising',     motif: true  },
  { name: 'haze',    mul: { pad: 0.8, move: 0.7, drone: 1,   wind: 1.4, sparks: 0.5,  bright: 0.6, verb: 1.4 }, register: 'fifth, deep', voicing: 'close', cycle: 'i v',        motif: false },
  { name: 'dusk',    mul: { pad: 0.5, move: 0.6, drone: 1.1, wind: 1,   sparks: 0.4,  bright: 0.8, verb: 1.2 }, register: 'root, deep',  voicing: 'low',   cycle: 'i v',        motif: false },
  { name: 'night',   mul: { pad: 0.2, move: 0.3, drone: 1.2, wind: 0.6, sparks: 0.15, bright: 0.5, verb: 1   }, register: 'root, deep',  voicing: 'low',   cycle: 'one chord',  motif: false },
];
export const NO_MUL: PhaseMul = { pad: 1, move: 1, drone: 1, wind: 1, sparks: 1, bright: 1, verb: 1 };

/** motif arpeggio patterns — up, down, up-down, outside-in — as an order over the chord's three
 *  tones (indices into `Chord.d`); a rest closes every four-step cycle */
export const MOTIF_PATTERNS: readonly (readonly number[])[] = [[0, 1, 2], [2, 1, 0], [0, 2, 1], [2, 0, 1]];

// ---------- arithmetic ----------
export function mtof(midi: number): number { return 440 * Math.pow(2, (midi - 69) / 12); }

/** semitones above the root of scale degree `deg`, wrapping through octaves either way */
export function degreeSemis(scale: readonly number[], deg: number): number {
  const n = scale.length, o = Math.floor(deg / n);
  return scale[deg - o * n] + 12 * o;
}

/** the ring a point is on: its distance to the tonic centre in ring widths, clamped to the field */
export function fieldDegree(distPx: number, ring: number): number {
  const d = Math.round(distPx / ring);
  return d < 0 ? 0 : d > MAX_DEG ? MAX_DEG : d;
}

const RELATIVE: Partial<Record<ScaleName, readonly [ScaleName, number]>> = {
  'major pentatonic': ['minor pentatonic', 9],
  'minor pentatonic': ['major pentatonic', 3],
};

/**
 * Where the key goes when it leaves home. `relative` keeps the same five notes and moves the
 * tonic (major → its relative minor on the sixth, minor → its relative major on the third); a
 * mode with no relative partner falls through to `fifths`, which moves the root a fifth in
 * direction `dir` so exactly one of the five notes changes. `none` never leaves.
 */
export function driftAway(mode: ScaleName, drift: Drift, dir: 1 | -1): { mode: ScaleName; offset: number } | null {
  if (drift === 'none') return null;
  const rel = drift === 'relative' ? RELATIVE[mode] : undefined;
  if (rel) return { mode: rel[0], offset: rel[1] };
  return { mode, offset: dir * 7 };
}

/** the pitch classes (0–11) a root and scale put in the sky — what a drift must keep or change */
export function pitchClasses(root: number, scale: readonly number[]): Set<number> {
  return new Set(scale.map(s => ((root + s) % 12 + 12) % 12));
}
